import { Fragment, ReactNode, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "@/components/CustomDatePicker";
import DebouncedTextField from "@/components/DebouncedTextField";
import MaskInput from "@/components/MaskInput";
import Text from "@/components/Text";
import { DATE_FORMAT } from "@/utils/date-time-utility";
import {
  DETAIL_CARD_BACKGROUND,
  FREE_TEXT_PLACEHOLDER,
  FREE_TEXT_MAX_ROWS,
  OPTION_CONTROL_SX,
  OPTION_ROW_SX,
} from "../readmission-f2f-constant";
import { GroupValue } from "../readmission-f2f-types";
import { toggleGroupOption } from "../readmission-f2f-utils";

export interface MultiSelectGroupProps {
  title: string;
  options: string[];
  value: GroupValue;
  onChange: (value: GroupValue) => void;
  columns?: number;
  exclusive?: Record<string, string[]>;
  withCatchAll?: boolean;
  withDetails?: boolean;
  detailPlaceholder?: string;
  optionDetailPlaceholders?: Record<string, string>;
  optionDetailDefaults?: Record<string, string>;
  hideTitle?: boolean;
  filter?: string;
  optionDateDetails?: string[];
  children?: ReactNode;
  disabled?: boolean;
  freeTextPlaceholder?: string;
  freeTextLabel?: string;
  freeTextRows?: number;
  inputMask?: string;
  inputMaskError?: string;
  /** Inline message under the title, e.g. when a save needs a selection. */
  error?: string;
}

const MASK_DIGIT_SLOT = /9/g;
const NON_DIGIT = /\D/g;

const MultiSelectGroup = ({
  title,
  options,
  value,
  onChange,
  columns = 0,
  exclusive,
  withCatchAll = true,
  withDetails = true,
  detailPlaceholder,
  optionDetailPlaceholders,
  optionDetailDefaults,
  hideTitle = false,
  filter,
  optionDateDetails,
  children,
  disabled,
  freeTextPlaceholder = FREE_TEXT_PLACEHOLDER,
  freeTextLabel,
  freeTextRows,
  inputMask,
  inputMaskError,
  error,
}: MultiSelectGroupProps) => {
  const [search, setSearch] = useState("");
  const [isMaskTouched, setIsMaskTouched] = useState(false);
  const maskSlots = inputMask?.match(MASK_DIGIT_SLOT)?.length ?? 0;
  const typedDigits = value.freeText.replace(NON_DIGIT, "").length;
  const maskError =
    isMaskTouched && typedDigits > 0 && typedDigits < maskSlots
      ? inputMaskError
      : undefined;
  const visibleOptions = filter
    ? options.filter(
        (option) =>
          !search.trim() ||
          value.selected.includes(option) ||
          option.toLowerCase().includes(search.trim().toLowerCase())
      )
    : options;
  const setDetail = (option: string, text: string) =>
    onChange({ ...value, details: { ...value.details, [option]: text } });

  const toggle = (option: string) => {
    const next = toggleGroupOption(value, option, exclusive);
    const defaultDetail = optionDetailDefaults?.[option];
    const isNewlySelected =
      next.selected.includes(option) && !value.selected.includes(option);
    onChange(
      defaultDetail && isNewlySelected
        ? { ...next, details: { ...next.details, [option]: defaultDetail } }
        : next
    );
  };

  return (
    <Box
      sx={{ border: "1px solid #E0E0E0", borderRadius: 1, p: 1.5 }}
      display="flex"
      flexDirection="column"
      gap={1}
    >
      {!hideTitle && <Text fontWeight="bold">{title}</Text>}
      {error && (
        <Text size={12} c="error.main">
          {error}
        </Text>
      )}
      {filter && (
        <TextField
          size="small"
          fullWidth
          placeholder={filter}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <Box
        display={columns ? "grid" : "flex"}
        flexWrap="wrap"
        gridTemplateColumns={
          columns ? `repeat(${columns}, minmax(0, 1fr))` : undefined
        }
        gap={columns ? 1 : 2}
      >
        {visibleOptions.map((option) => {
          const isSelected = value.selected.includes(option);
          const placeholder =
            optionDetailPlaceholders?.[option] ?? detailPlaceholder;
          // Per-option placeholders mean only those options take a detail box.
          const isDate = !!optionDateDetails?.includes(option);
          const hasDetail =
            withDetails &&
            isSelected &&
            (isDate || !(optionDetailPlaceholders && !placeholder));
          return (
            <Fragment key={option}>
              <FormControlLabel
                sx={OPTION_ROW_SX}
                control={
                  <Checkbox
                    size="small"
                    disabled={disabled}
                    checked={isSelected}
                    onChange={() => toggle(option)}
                    sx={OPTION_CONTROL_SX}
                  />
                }
                label={<Text>{option}</Text>}
              />
              {/* Full-width so the box drops under its option, ahead of the next one. */}
              {hasDetail && (
                <Box
                  sx={{
                    bgcolor: DETAIL_CARD_BACKGROUND,
                    borderRadius: 1,
                    p: 1.25,
                    width: "100%",
                    gridColumn: columns ? "1 / -1" : undefined,
                  }}
                >
                  {optionDateDetails?.includes(option) ? (
                    <CustomDatePicker
                      placeholder={DATE_FORMAT}
                      maxDate={dayjs()}
                      value={
                        value.details[option]
                          ? dayjs(value.details[option], DATE_FORMAT)
                          : null
                      }
                      onChange={(date) =>
                        setDetail(option, date ? date.format(DATE_FORMAT) : "")
                      }
                    />
                  ) : (
                    <DebouncedTextField
                      size="small"
                      fullWidth
                      multiline
                      maxRows={FREE_TEXT_MAX_ROWS}
                      placeholder={placeholder}
                      value={value.details[option] ?? ""}
                      onChange={(text) => setDetail(option, text)}
                    />
                  )}
                </Box>
              )}
            </Fragment>
          );
        })}
      </Box>

      {children}

      {withCatchAll && inputMask && (
        <MaskInput
          mask={inputMask}
          disabled={disabled}
          placeholder={inputMask.replace(MASK_DIGIT_SLOT, "x")}
          value={value.freeText}
          error={maskError}
          onBlur={() => setIsMaskTouched(true)}
          onChange={(e) => onChange({ ...value, freeText: e.target.value })}
        />
      )}
      {withCatchAll && !inputMask && (
        <>
          {freeTextLabel && <Text fontWeight="bold">{freeTextLabel}</Text>}
          <DebouncedTextField
            size="small"
            fullWidth
            multiline
            minRows={freeTextRows}
            maxRows={Math.max(FREE_TEXT_MAX_ROWS, freeTextRows ?? 0)}
            disabled={disabled}
            placeholder={freeTextPlaceholder}
            value={value.freeText}
            onChange={(freeText) => onChange({ ...value, freeText })}
          />
        </>
      )}
    </Box>
  );
};

export default MultiSelectGroup;
